import React, { useMemo, useState, useEffect, useRef } from "react";
import "./Capabilities.css";
import capabilityClusterMap from "../../data/capabilityClusterMap.json";
import TOCModal from "./TOCModal";
// import ImageMapHelper from '../../utils/ImageMapHelper';

const DEFAULT_PREVIEW = "/Images/Capabilities/capabilities-preview.png";
const CLUSTER_IMG_BASE = "/Images/Cluster_Images";

const uiText = {
  hero: {
    titlePrimary: "Your Capabilities.",
    titleAccent: "Your Path.",
    subtitle:
      "Discover relevant roles and learning clusters tailored to your expertise",
    backgroundImageUrl: "/Images/Capabilities/capabilities-hero.jpg",
  },
  instructions: {
    title: "Step By Step Instruction",
    intro:
      "Each capability is mapped to defined roles and skill clusters to ensure focused and role-aligned learning.",
    bullet:
      "Every capability is structured into roles, and each role is mapped to one or more learning clusters with clearly defined skill requirements.",
    stepsTitle: "Follow these simple steps to identify your assigned cluster",
    steps: [
      {
        step: "Step 1",
        heading: "Select Your Capability",
        description: "Choose a capability from the list",
      },
      {
        step: "Step 2",
        heading: "Select Role",
        description: "Select a role under that capability",
      },
      {
        step: "Step 3",
        heading: "Clusters Auto-Load",
        description: "Clusters appear automatically for the selected role",
      },
    ],
  },
  filters: {
    heading: "To view cluster(s)",
    capabilityPlaceholder: "Select / Search Capability",
    rolePlaceholder: "Select Role",
    // buttonText removed from UI, but keeping property is harmless
    buttonText: "View Cluster(s)",
    hint: "* Select capability and role to view clusters",
  },
};

function IconChevron({ direction = "down" }) {
  const rotate = direction === "up" ? "180deg" : "0deg";
  return (
    <svg
      className="icon"
      style={{ transform: `rotate(${rotate})` }}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M7 10l5 5 5-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconArrowRight() {
  return (
    <svg
      className="icon"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M5 12h12M13 6l6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function Capabilities() {
  const data = capabilityClusterMap;
  const [instructionOpen, setInstructionOpen] = useState(true);
  const [hoveredArea, setHoveredArea] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);

  // Defaults (you can keep your preferred defaults)
  const [selectedCapabilityName, setSelectedCapabilityName] = useState("SAP");
  const [selectedRoleName, setSelectedRoleName] = useState(
    "SAP Finance Engineer",
  );

  const [visibleClusters, setVisibleClusters] = useState([]);
  const [activeClusterId, setActiveClusterId] = useState("");

  // For image fallback (png -> jpg -> jpeg)
  const [imgSrc, setImgSrc] = useState(DEFAULT_PREVIEW);
  const [imgTry, setImgTry] = useState(0);

  const selectedCapability = useMemo(() => {
    return (
      data.capabilities.find((c) => c.name === selectedCapabilityName) ||
      data.capabilities[0]
    );
  }, [data.capabilities, selectedCapabilityName]);

  const roleOptions = useMemo(() => {
    return (selectedCapability?.roles || []).map((r) => r.name);
  }, [selectedCapability]);

  // When capability changes: ensure role is valid and reset selection
  useEffect(() => {
    if (!roleOptions.includes(selectedRoleName)) {
      setSelectedRoleName(roleOptions[0] || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCapabilityName]);

  const selectedRoleObj = useMemo(() => {
    return (selectedCapability?.roles || []).find(
      (r) => r.name === selectedRoleName,
    );
  }, [selectedCapability, selectedRoleName]);

  // ✅ AUTO-LOAD CLUSTERS whenever role changes (no button needed)
  useEffect(() => {
    const clustersForRole = selectedRoleObj?.clusters || [];
    setVisibleClusters(clustersForRole);

    const firstId = clustersForRole[0]?.id || "";
    setActiveClusterId(firstId);

    // Reset preview while switching role
    if (!firstId) {
      setImgSrc(DEFAULT_PREVIEW);
      setImgTry(0);
    }
  }, [selectedRoleObj]);

  const activeCluster = useMemo(() => {
    return (
      (visibleClusters || []).find((c) => c.id === activeClusterId) ||
      visibleClusters[0]
    );
  }, [visibleClusters, activeClusterId]);

  // Build URL safely: supports filenames with spaces/&/– and supports missing extension
  const buildClusterUrl = (imageFile, tryIndex = 0) => {
    if (!imageFile) return DEFAULT_PREVIEW;

    const tries = ["png", "jpg", "jpeg"];
    const ext = tries[Math.min(tryIndex, tries.length - 1)];

    // If imageFile already contains an extension, use it directly.
    const hasExt = /\.(png|jpg|jpeg)$/i.test(imageFile);
    const fileName = hasExt ? imageFile : `${imageFile}.${ext}`;

    return `${CLUSTER_IMG_BASE}/${encodeURIComponent(fileName)}`;
  };

  // Update image src when active cluster changes
  useEffect(() => {
    const nextSrc = buildClusterUrl(activeCluster?.imageFile, 0);
    setImgSrc(nextSrc);
    setImgTry(0);
  }, [activeCluster]);

  // Keeping old handler (not used now) so code is not lost
  const handleViewClusters = (e) => {
    e?.preventDefault?.();
    const clustersForRole = selectedRoleObj?.clusters || [];
    setVisibleClusters(clustersForRole);
    setActiveClusterId(clustersForRole[0]?.id || "");
  };

  const handleImgError = () => {
    // Try next extension if not yet tried
    const nextTry = imgTry + 1;
    if (activeCluster?.imageFile && nextTry <= 2) {
      setImgTry(nextTry);
      setImgSrc(buildClusterUrl(activeCluster.imageFile, nextTry));
    } else {
      setImgSrc(DEFAULT_PREVIEW);
    }
  };

  const handleAreaClick = (e, area) => {
    e.preventDefault();
    
    // If area has modal data, show modal instead of opening link
    if (area.showModal && area.tocData) {
      setModalData(area.tocData);
      setModalOpen(true);
    } else if (area.link) {
      // Open in popup window
      const width = 1024;
      const height = 768;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      window.open(
        area.link,
        '_blank',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );
    }
  };

  const handleAreaMouseEnter = (e, area) => {
    const rect = imageRef.current?.getBoundingClientRect();
    if (rect) {
      setTooltipPos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
    setHoveredArea(area);
  };

  const handleAreaMouseMove = (e) => {
    const rect = imageRef.current?.getBoundingClientRect();
    if (rect && hoveredArea) {
      setTooltipPos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleAreaMouseLeave = () => {
    setHoveredArea(null);
  };

  return (
    <main className="cap-page">
      {/* TOC Modal */}
      <TOCModal 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)} 
        data={modalData} 
      />

      {/* HERO */}
      <section
        className="cap-hero"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(0,0,0,.75) 0%, rgba(0,0,0,.15) 65%, rgba(0,0,0,.15) 100%), url(${uiText.hero.backgroundImageUrl})`,
        }}
        aria-label="Capabilities hero"
      >
        <div className="cap-hero__inner">
          <h1 className="cap-hero__title">
            <span className="cap-hero__titlePrimary">
              {uiText.hero.titlePrimary}
            </span>{" "}
            <span className="cap-hero__titleAccent">
              {uiText.hero.titleAccent}
            </span>
          </h1>
          <p className="cap-hero__subtitle">{uiText.hero.subtitle}</p>
        </div>
      </section>

      {/* BODY */}
      <section className="cap-container">
        {/* Instruction Accordion */}
        <div className="cap-card cap-instruction">
          <button
            type="button"
            className="cap-accordionHeader"
            onClick={() => setInstructionOpen((s) => !s)}
            aria-expanded={instructionOpen}
          >
            <div className="cap-accordionHeader__text">
              {uiText.instructions.title}
            </div>
            <span className="cap-accordionHeader__iconBox" aria-hidden="true">
              <IconChevron direction={instructionOpen ? "up" : "down"} />
            </span>
          </button>

          {instructionOpen && (
            <div className="cap-accordionBody">
              <p className="cap-muted">{uiText.instructions.intro}</p>

              <div className="cap-bulletRow">
                <span className="cap-bulletDot" aria-hidden="true">
                  i
                </span>
                <p className="cap-bulletText">{uiText.instructions.bullet}</p>
              </div>

              <div className="cap-divider" />

              <div className="cap-stepsTitle">
                {uiText.instructions.stepsTitle}
              </div>

              <div className="cap-steps">
                {uiText.instructions.steps.map((s, idx) => (
                  <React.Fragment key={s.step}>
                    <div className="cap-step">
                      <div className="cap-step__circle">
                        <span className="cap-step__circleText">{s.step}</span>
                      </div>
                      <div className="cap-step__info">
                        <div className="cap-step__heading">{s.heading}</div>
                        <div className="cap-step__desc">{s.description}</div>
                      </div>
                    </div>

                    {idx !== uiText.instructions.steps.length - 1 && (
                      <div className="cap-stepArrow" aria-hidden="true">
                        <IconArrowRight />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Filters */}
        <h2 className="cap-sectionTitle">{uiText.filters.heading}</h2>

        {/* ✅ Not a form anymore */}
        <div className="cap-filters">
          {/* Capability */}
          <label className="cap-field">
            <span className="cap-label">
              {uiText.filters.capabilityPlaceholder}
            </span>
            <div className="cap-inputWrap">
              <select
                className="cap-input"
                value={selectedCapabilityName}
                onChange={(e) => setSelectedCapabilityName(e.target.value)}
              >
                <option value="S&PE">S&PE</option>
                <option value="Data & AI">Data & AI</option>
                <option value="SAP">SAP</option>
                {/* <option value="SAP Commerce">SAP Commerce</option>
                <option value="SAP BTP">SAP BTP</option>
                <option value="SAP Data">SAP Data</option>
                <option value="SAP Integration">SAP Integration</option> */}
                <option value="Oracle">Oracle</option>
                <option value="IE">IE</option>
                <option value="QE">QE</option>
                <option value="IX">IX</option>
                {/* <option value="Microsoft">Microsoft</option>
                <option value="Security">Security</option>
                <option value="Salesforce">Salesforce</option> */}
              </select>
            </div>
          </label>

          {/* Role */}
          <label className="cap-field">
            <span className="cap-label">{uiText.filters.rolePlaceholder}</span>
            <select
              className="cap-select"
              value={selectedRoleName}
              onChange={(e) => setSelectedRoleName(e.target.value)}
            >
              {roleOptions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>

          <div className="cap-hint">{uiText.filters.hint}</div>
        </div>

        {/* Cluster list + Image */}
        <div className="cap-grid">
          <aside className="cap-left">
            <div className="cap-clusterList" role="list">
              {visibleClusters.length === 0 ? (
                <div className="cap-emptyState">
                  Select a capability and role to see clusters.
                </div>
              ) : (
                visibleClusters.map((c) => {
                  const isActive = c.id === activeCluster?.id;
                  return (
                    <button
                      type="button"
                      key={c.id}
                      className={`cap-clusterItem ${isActive ? "is-active" : ""}`}
                      onClick={() => setActiveClusterId(c.id)}
                      role="listitem"
                    >
                      <span className="cap-clusterItem__text">{c.name}</span>
                      <span
                        className="cap-clusterItem__icon"
                        aria-hidden="true"
                      >
                        <IconArrowRight />
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </aside>

          <section className="cap-right" aria-label="Cluster preview">
            {/* Coordinate Helper Tool - Remove this after getting coordinates */}
            {/* <ImageMapHelper imageSrc={imgSrc} /> */}
            
            <div className="cap-preview" style={{ position: 'relative' }}>
              <img
                ref={imageRef}
                className="cap-preview__img"
                src={imgSrc}
                alt="Capabilities preview"
                loading="lazy"
                onError={handleImgError}
                useMap={activeCluster?.imageMap ? `#${activeCluster.id}-map` : undefined}
              />
              {activeCluster?.imageMap && (
                <>
                  <map name={`${activeCluster.id}-map`}>
                    {activeCluster.imageMap.areas.map((area, index) => (
                      <area
                        key={index}
                        shape={area.shape}
                        coords={area.coords}
                        href={area.link}
                        alt={area.alt || `Clickable area ${index + 1}`}
                        onClick={(e) => handleAreaClick(e, area)}
                        onMouseEnter={(e) => handleAreaMouseEnter(e, area)}
                        onMouseMove={handleAreaMouseMove}
                        onMouseLeave={handleAreaMouseLeave}
                        style={{ cursor: 'pointer' }}
                      />
                    ))}
                  </map>
                  {hoveredArea && (
                    <div
                      className="cap-tooltip"
                      style={{
                        position: 'absolute',
                        left: `${tooltipPos.x + 10}px`,
                        top: `${tooltipPos.y + 10}px`,
                        pointerEvents: 'none',
                        zIndex: 1000
                      }}
                    >
                      {hoveredArea.alt}
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}
